import { useEffect, useState } from "react";
import {
  getTenders,
  getTenderDocs,
  getAmmendmentDocs,
  formatDate,
  getTendersForSearch
} from "./util.js";
import "./App.css";

import pdfIcon from "./image/pdf.gif";

export default function TenderList() {
  const isArchivePage = window.location.pathname.includes("tender-archive");

  const [rows, setRows]                       = useState([]);
  const [page, setPage]                       = useState(1);
  const [pageSize, setPageSize]               = useState(10);
  const [total, setTotal]                     = useState(0);
  const [loading, setLoading]                 = useState(false);
  const [search, setSearch]                   = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  async function load() {
    setLoading(true);
    setRows([]);

    const response = debouncedSearch === ""
      ? await getTenders(page, pageSize)
      : await getTendersForSearch(page, pageSize, debouncedSearch);

    const { items = [], totalCount = 0 } = response;
    const enriched = [];

    for (const t of items) {
      let docs = [], amendments = [];
      try { docs       = await getTenderDocs(t.tenderId);     } catch {}
      try { amendments = await getAmmendmentDocs(t.tenderId); } catch {}

      enriched.push({
        ...t,
        documents: docs.map(d => ({
          url:  (d.fileEntryEnglish?.link?.href ?? "").replace("&download=true", "download=false"),
          name: d.nameEnglish ?? "",
        })),
        amendments: amendments.map(a => ({
          url:  (a.fileEntryEnglish?.link?.href ?? "").replace("download=true", "download=false"),
          name: a.nameEnglish ?? "",
        })),
      });
    }

    setRows(enriched);
    setTotal(totalCount);
    setLoading(false);
  }

  useEffect(() => { load(); }, [page, pageSize, debouncedSearch]);

  const totalPages = Math.ceil(total / pageSize) || 1;

  return (
    <>
      {/* TOP CONTROLS */}
      <div className="tender-controls">
        <div className="tender-controls-inner">

          {/* LEFT: Page size */}
          <div className="tender-controls-left">
            <label>Select Page Size:</label>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>

          {/* RIGHT: Archive button + Search */}
          <div className="tender-controls-right">
            {!isArchivePage && (
              <button
                type="button"
                className="btn btn-primary tender-archive-btn"
                onClick={() => (window.location.href = "/tender-archive")}
              >
                Archive Tender
              </button>
            )}

            <div className="tender-search-row">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by description, ref no, issued by…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
          </div>

        </div>
      </div>

      {/* TABLE — wrapper for horizontal scroll on mobile */}
      <div className="tender-table-wrapper">
        <table className="tender-table">
          <thead>
            <tr>
              <th>Description Of Tender / RFP</th>
              <th>Ref No</th>
              <th>Tender Date</th>
              <th>Issued By</th>
              <th>Last Date</th>
              <th>Document</th>
              <th>Amendment / Pre-Bid</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: 12 }}>
                  Loading...
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: 12 }}>
                  No data available
                </td>
              </tr>
            )}

            {!loading && rows.map((r) => (
              <tr key={r.tenderId}>
                <td>{r.descriptionEnglish}</td>
                <td>{r.tenderRefNo}</td>
                <td>{formatDate(r.tenderDate)}</td>
                <td>{r.issuedBy}</td>
                <td>{formatDate(r.lastDate)}</td>

                <td>
                  {r.documents.length ? (
                    <div className="doc-stack">
                      {r.documents.map((doc, i) => (
                        <a key={i} href={doc.url} target="_blank" className="doc-link">
                          {doc.name}
                          <img src={pdfIcon} alt="PDF" className="pdf-icon" />
                        </a>
                      ))}
                    </div>
                  ) : "—"}
                </td>

                <td>
                  {r.amendments.length ? (
                    <div className="doc-stack">
                      {r.amendments.map((doc, i) => (
                        <a key={i} href={doc.url} target="_blank" className="doc-link">
                          {doc.name}
                          <img src={pdfIcon} alt="PDF" className="pdf-icon" />
                        </a>
                      ))}
                    </div>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="tender-pagination">
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
          Previous
        </button>
        <span>Page {page} of {totalPages}</span>
        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
          Next
        </button>
      </div>
    </>
  );
}